"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import Image from "next/image";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
} from "lucide-react";
import { impactCategories as staticImpactCategories } from "@/content/impact";
import type { ImpactCategoryContent } from "@/lib/api-types";
import { LinkButton } from "@/components/ui/LinkButton";
import { Card, CardBody } from "@/components/ui/Card";
import { cn } from "@/lib/utils";

const AUTO_INTERVAL_MS = 10_000;

function SectorContent({ category }: { category: ImpactCategoryContent }) {
  return (
    <div className="flex h-full flex-col">
      <h3 className="mb-1 break-words text-xl font-bold text-foreground">
        {category.id}
      </h3>
      <p className="mb-3 line-clamp-3 break-words text-sm text-muted">
        {category.summary}
      </p>

      <ul className="space-y-1.5">
        {category.programs.map((program) => (
          <li key={program}>
            <div className="group flex items-center gap-2 rounded-lg border border-border/60 bg-card px-3 py-2 text-sm transition-colors hover:border-primary/30 hover:bg-card/80">
              <ChevronRight
                size={14}
                className="shrink-0 text-muted transition-colors group-hover:text-primary"
                aria-hidden
              />
              <span className="text-foreground">{program}</span>
            </div>
          </li>
        ))}
      </ul>

      <LinkButton
        href={`/programs/${category.slug}`}
        variant="accent"
        size="sm"
        className="mt-4 w-full sm:w-auto"
      >
        Explore {category.id} program
      </LinkButton>
    </div>
  );
}

type ImpactSectorShowcaseProps = {
  categories?: ImpactCategoryContent[];
};

export function ImpactSectorShowcase({
  categories = staticImpactCategories.map((category) => ({
    id: category.id,
    slug: category.slug,
    summary: category.summary,
    programs: [...category.programs],
    image: category.image,
  })),
}: ImpactSectorShowcaseProps) {
  const reduced = useReducedMotion();
  const liveRegionId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const total = categories.length || 1;

  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocusedWithin, setIsFocusedWithin] = useState(false);
  const [progressKey, setProgressKey] = useState(0);

  const category = categories[activeIndex] ?? categories[0];
  const autoEnabled =
    !reduced && !isPaused && !isHovered && !isFocusedWithin;

  const goToIndex = useCallback(
    (index: number) => {
      setActiveIndex((index + total) % total);
      setProgressKey((k) => k + 1);
    },
    [total]
  );

  const advance = useCallback(
    (direction: 1 | -1) => {
      goToIndex(activeIndex + direction);
    },
    [activeIndex, goToIndex]
  );

  useEffect(() => {
    if (!autoEnabled) return;

    const timer = window.setInterval(() => {
      setActiveIndex((i) => (i + 1) % total);
      setProgressKey((k) => k + 1);
    }, AUTO_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [autoEnabled, progressKey, total]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLElement &&
        (e.target.tagName === "INPUT" ||
          e.target.tagName === "TEXTAREA" ||
          e.target.isContentEditable)
      ) {
        return;
      }

      if (e.key === "ArrowRight") {
        e.preventDefault();
        advance(1);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        advance(-1);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [advance]);

  const handleFocusIn = () => setIsFocusedWithin(true);
  const handleFocusOut = (e: React.FocusEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(e.relatedTarget as Node)
    ) {
      setIsFocusedWithin(false);
    }
  };

  if (!category) {
    return null;
  }

  return (
    <div className="mx-auto max-w-5xl">
      <div
        id={liveRegionId}
        className="sr-only"
        aria-live="polite"
        aria-atomic="true"
      >
        {category.id}, sector {activeIndex + 1} of {total}
      </div>

      <div
        ref={containerRef}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocusCapture={handleFocusIn}
        onBlurCapture={handleFocusOut}
      >
      <Card className="overflow-hidden">
        <CardBody className="p-4 md:p-5">
          <div className="grid gap-4 lg:grid-cols-2 lg:items-start lg:gap-6">
            <div className="relative aspect-[4/3] min-h-[240px] overflow-hidden rounded-2xl border border-border lg:min-h-[320px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={category.id}
                  initial={reduced ? false : { opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={reduced ? undefined : { opacity: 0 }}
                  transition={{ duration: reduced ? 0 : 0.35 }}
                  className="absolute inset-0"
                >
                  <Image
                    src={category.image}
                    alt={`${category.id} impact sector`}
                    fill
                    className="object-cover"
                    sizes="(max-width: 1024px) 100vw, 480px"
                    priority={activeIndex === 0}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
                  <p className="absolute bottom-4 left-4 z-10 text-xs font-semibold uppercase tracking-wider text-white/90">
                    Sector {activeIndex + 1} of {total}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="flex min-h-0 flex-col lg:border-l lg:border-border lg:pl-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={category.id}
                  initial={reduced ? false : { opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={reduced ? undefined : { opacity: 0 }}
                  transition={{ duration: reduced ? 0 : 0.35 }}
                  className="flex flex-1 flex-col"
                >
                  <SectorContent category={category} />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </CardBody>
      </Card>
      </div>

      <div className="mt-8 flex flex-col items-center gap-4">
        <div className="h-1 w-full max-w-md overflow-hidden rounded-full bg-border">
          <motion.div
            key={`${progressKey}-${autoEnabled}`}
            className="h-full origin-left rounded-full bg-primary"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: autoEnabled ? 1 : 0 }}
            transition={
              autoEnabled
                ? { duration: AUTO_INTERVAL_MS / 1000, ease: "linear" }
                : { duration: 0 }
            }
          />
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => advance(-1)}
            aria-label="Previous sector"
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background text-foreground transition-colors hover:bg-card"
          >
            <ChevronLeft size={18} aria-hidden />
          </button>

          <span className="min-w-[5rem] text-center text-sm font-medium text-muted">
            {activeIndex + 1} of {total}
          </span>

          <button
            type="button"
            onClick={() => advance(1)}
            aria-label="Next sector"
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background text-foreground transition-colors hover:bg-card"
          >
            <ChevronRight size={18} aria-hidden />
          </button>

          {!reduced && (
            <button
              type="button"
              onClick={() => {
                setIsPaused((p) => !p);
                setProgressKey((k) => k + 1);
              }}
              aria-label={isPaused ? "Play auto-advance" : "Pause auto-advance"}
              aria-pressed={isPaused}
              className="flex h-9 items-center justify-center gap-1.5 rounded-lg border border-border bg-background px-3 text-sm font-medium text-foreground transition-colors hover:bg-card"
            >
              {isPaused ? (
                <>
                  <Play size={16} aria-hidden />
                  Play
                </>
              ) : (
                <>
                  <Pause size={16} aria-hidden />
                  Pause
                </>
              )}
            </button>
          )}
        </div>

        <div
          className="flex flex-wrap items-center justify-center gap-2"
          role="tablist"
          aria-label="Impact sectors"
        >
          {categories.map((item, index) => (
            <button
              key={item.slug}
              type="button"
              role="tab"
              aria-selected={index === activeIndex}
              aria-label={`${item.id}, sector ${index + 1}`}
              onClick={() => goToIndex(index)}
              className={cn(
                "h-2.5 rounded-full transition-all duration-300",
                index === activeIndex
                  ? "w-6 bg-primary"
                  : "w-2.5 bg-border hover:bg-muted"
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
